import React from "react";
import VASGraph from "../../../../components/feedback/VASGraph";
import { toVoiceAssessmentVASGraphProps } from "./voiceAssessmentGraphPresentation";
import FeedbackMetricSummary from "./FeedbackMetricSummary";

const formatOneDecimal = value => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "Not available";
  return numeric.toFixed(1).replace(/\.0$/, "");
};

function observedRangeInterpretation(learnerValue, reference) {
  const learner = Number(learnerValue);
  const values = (reference?.values || []).map(Number).filter(Number.isFinite);
  const minimum = Number.isFinite(Number(reference?.min)) ? Number(reference.min) : Math.min(...values);
  const maximum = Number.isFinite(Number(reference?.max)) ? Number(reference.max) : Math.max(...values);
  if (!Number.isFinite(learner) || !Number.isFinite(minimum) || !Number.isFinite(maximum)) return null;
  return learner >= minimum && learner <= maximum
    ? `Your rating is within the observed expert range (${formatOneDecimal(minimum)}–${formatOneDecimal(maximum)}), which means it falls within the range of expert opinion.`
    : `Your rating is outside the observed expert range (${formatOneDecimal(minimum)}–${formatOneDecimal(maximum)}). This is descriptive feedback, not a score or pass/fail result.`;
}

export default function ContinuousReferenceFeedback({ rows = [], sourceLabel, sourceHelp, graphical = false, compact = false }) {
  const available = rows.filter(row => row.reference);
  const sectionLevelHelp = graphical && compact;
  if (!available.length) return null;
  return (
    <section className="av-continuous-feedback" aria-label="Continuous reference feedback">
      <div className="av-methodology-heading">
        <h3>Reference feedback</h3>
        {sectionLevelHelp && sourceHelp}
      </div>
      {!sectionLevelHelp && <div className="av-methodology-heading av-continuous-feedback__source-heading">
        <span>{sourceLabel}</span>
        {sourceHelp}
      </div>}
      <div className="av-continuous-feedback__rows">
        {available.map(({ constructLabel, sourceParameterLabel, learnerValue, reference }) => {
          const rangeInterpretation = graphical ? observedRangeInterpretation(learnerValue, reference) : null;
          return <article key={sourceParameterLabel}>
            <h4>{constructLabel}</h4>
            {!compact && <p>{sourceParameterLabel} — continuous reference</p>}
            {graphical && reference.values?.length > 0 && <VASGraph
              {...toVoiceAssessmentVASGraphProps({
                attributeName: constructLabel,
                learnerValue,
                referenceValues: reference.values,
              })}
              ariaLabel={`${constructLabel} learner and PVQD reference ratings`}
            />}
            {learnerValue !== undefined && !compact && <p>Your rating: {Math.round(Number(learnerValue))}</p>}
            {rangeInterpretation && <p className="av-continuous-feedback__range-interpretation">{rangeInterpretation}</p>}
            {compact ? <FeedbackMetricSummary items={[
              { label: "Your rating", value: learnerValue === undefined ? "Not available" : Math.round(Number(learnerValue)) },
              { label: "Central value", value: formatOneDecimal(reference.mean) },
              { label: "Spread (SD)", value: formatOneDecimal(reference.sd) },
            ]} /> : <dl>
              {learnerValue !== undefined && <><dt>Your rating</dt><dd>{Math.round(Number(learnerValue))}</dd></>}
              <dt>Central value</dt><dd>{formatOneDecimal(reference.mean)}</dd>
              <dt>Spread (SD)</dt><dd>{formatOneDecimal(reference.sd)}</dd>
              {!compact && <><dt>Expert observations (N)</dt><dd>{Math.trunc(Number(reference.n))}</dd></>}
              {!compact && reference.min !== undefined && reference.max !== undefined && <><dt>Source range</dt><dd>{formatOneDecimal(reference.min)}–{formatOneDecimal(reference.max)}</dd></>}
            </dl>}
          </article>;
        })}
      </div>
    </section>
  );
}

export { formatOneDecimal, observedRangeInterpretation };
